import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL, FALLBACK_API_URL } from '../config';

const PlacementTechInterviewPage = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [interviewConfig, setInterviewConfig] = useState(null);
  const [isResuming, setIsResuming] = useState(false);

  const session = SessionManager.getSession('candidate');
  const apiUrl = API_BASE_URL || FALLBACK_API_URL;

  useEffect(() => {
    if (!session) {
      navigate('/login');
      return;
    }
    startInterview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const startInterview = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${apiUrl}/placement-training/interview/start`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            assessment_id: assessmentId,
            interview_type: 'tech'
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setInterviewConfig(data);
        setIsResuming(data.is_resuming || false);
      } else {
        alert('Failed to start interview. Please try again.');
        navigate(`/placement-training/assessment/${assessmentId}`);
      }
    } catch (error) {
      console.error('Error starting interview:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !interviewConfig) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
              <Icon name="engineering" size={32} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Technical Interview</h1>
              <p className="text-gray-600">
                {interviewConfig.company_name} - {interviewConfig.position_title}
              </p>
            </div>
          </div>

          {isResuming && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-700">
                <Icon name="info" size={20} />
                <span className="font-medium">
                  Resuming interview - The AI will continue from where you left off
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Interview Instructions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Interview Guidelines</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Icon name="schedule" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Duration</div>
                <div className="text-sm text-gray-600">
                  {interviewConfig.duration_minutes} minutes total
                  {isResuming && ` (${Math.floor(interviewConfig.time_remaining_seconds / 60)} minutes remaining)`}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="mic" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Voice Interview</div>
                <div className="text-sm text-gray-600">
                  Speak clearly and answer questions based on your experience and the job role
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="psychology" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">AI Interviewer</div>
                <div className="text-sm text-gray-600">
                  The AI will ask technical questions related to your resume and the position
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Icon name="verified" size={20} className="text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Evaluation</div>
                <div className="text-sm text-gray-600">
                  Your responses will be evaluated for technical knowledge, clarity, and relevance
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Start Interview Button */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-2">
            {isResuming ? 'Resume Technical Interview' : 'Ready to Start?'}
          </h3>
          <p className="text-blue-100 mb-6">
            {isResuming 
              ? 'Continue your interview from where you left off'
              : 'Click below to begin your technical interview with the AI interviewer'
            }
          </p>
          <button
            onClick={() => {
              // Navigate to live interview page with session ID
              navigate(`/live-interview/${interviewConfig.interview_session_id}`, {
                state: {
                  type: 'placement_tech',
                  interviewType: 'placement_tech',  // For WebSocket
                  assessmentId,
                  roundId: interviewConfig.round_id,
                  duration: interviewConfig.duration_minutes,
                  timeRemaining: interviewConfig.time_remaining_seconds,
                  isResuming,
                  candidateName: session?.user?.name || 'Candidate',
                  jobTitle: interviewConfig.position_title || 'Position',
                  company: interviewConfig.company_name || 'Company',
                  language: 'en-US'
                }
              });
            }}
            className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
          >
            <Icon name={isResuming ? 'play_arrow' : 'mic'} size={24} />
            <span>{isResuming ? 'Resume Interview' : 'Start Interview'}</span>
          </button>
        </div>

        {/* Tips */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <Icon name="lightbulb" size={24} className="text-yellow-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-yellow-900 mb-2">Interview Tips</h3>
              <ul className="space-y-1 text-sm text-yellow-800">
                <li>• Ensure you're in a quiet environment with good internet connection</li>
                <li>• Allow microphone access when prompted</li>
                <li>• Speak clearly and at a moderate pace</li>
                <li>• Provide specific examples from your experience</li>
                <li>• If you don't know something, be honest and explain your approach to learning</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlacementTechInterviewPage;

